// src/modules/perfil.js — vista de perfil del jugador.
//
// Extraído del IIFE inline de index.html (sub-fase 3.7 del refactor).
//
// Esta es la versión CANÓNICA, fusión de:
//   - renderPerfilV2 que vivía en index.html líneas 611-682 (la que el
//     usuario veía en runtime gracias al patchProfileRoute con setTimeout(0))
//   - renderPerfil legacy que vivía en js/app.js línea 5604 (la que el
//     router central despachaba; renderPerfilV2 la sobreescribía 0ms después)
//
// La versión legacy se ELIMINA en este commit (no se archiva en /legacy/
// porque ya estaba enmascarada en runtime por V2; la versión que el
// usuario realmente veía era V2, no la legacy).
//
// Fix DT-1 (REFACTOR_AUDIT §13): si el código de grupo no existe en la DB,
// renderPerfil REDIRIGE A LANDING (`window.location.hash = '/'`) en lugar
// de tirar TypeError al leer .jugadores. Decisión: redirección, no vista
// degradada, porque (a) coincide con el patrón del router central para
// hash inválidos, (b) evita cascada de TypeErrors aguas abajo (bottom-nav
// apuntando a /g/<inexistente>), (c) UX coherente: si el grupo no existe
// el usuario vuelve al inicio.
//
// Dependencias salientes (post-3.16):
//   - de ../core/db.js: getNormalizedDB (import directo, para validar grupo)
//   - de ./perfil-helpers.js (NUEVO en 3.16): profileData, escapePM,
//     activeGroupBarPM, bottomNavPM, formatPM, historyCardsPM,
//     renderSavedTribunaPM (7 helpers migrados del IIFE inline,
//     eliminado en 3.16).
//
// ensureProfileStyles ELIMINADA en 3.16: el CSS que inyectaba
// dinámicamente está estático en css/perfil-runtime.css, cargado vía
// <link rel="stylesheet"> en <head> de index.html.
//
// IIFE inline DESMONTADO en 3.16 — fin de la deuda DT-9. Los imports
// son ES Modules directos, sin window.* lookup.

import { getNormalizedDB } from '../core/db.js'
import { getCurrentUser, signOut } from '../core/auth.js'
import {
  getPerfilCompleto, quitarArengaGuardada,
  reaccionar, quitarReaccion, eliminarArengaCompleta,
  puntosPorRitual, refrescarContadoresRitual,
} from '../core/rituales.js'
import { computeReactionDelta, invertDeltas, applyReactionDelta } from '../core/reaction-delta.js'
import { ensureBlobUrl, revokeBlobUrl } from '../core/audio-player.js'
import { formatTimeAgo } from '../utils/time-ago.js'
import { renderPactoIcon } from '../ui/icons.js'
import { renderArengaCard } from '../ui/arenga-card.js'
import { retryCardHTML, wireRetry } from '../ui/retry-card.js'
import { openShareModal } from '../ui/share-modal.js'
import { buildShareUrl } from '../core/storage-arengas.js'
import {
  profileData,
  escapePM,
  activeGroupBarPM,
  bottomNavPM,
  formatPM,
  historyCardsPM,
  renderSavedTribunaPM
} from './perfil-helpers.js'
import { buildPerfilSkeleton } from './perfil-skeleton.js'

// Tipos canónicos de arenga (spec v1.2 §2). Vocabulario idéntico a tribuna.js
// y rituales.js. Proxy con fallback 'vamos' para keys legacy desconocidas.
const PERFIL_ARENGA_TYPES_BASE = {
  vamos:       { label: '¡Vamos todavía!',     color: '#2563eb', icon: 'M9 18V5l9-2v13M9 9l9-2M9 18a2 2 0 1 1-2-2 2 2 0 0 1 2 2ZM18 16a2 2 0 1 1-2-2 2 2 0 0 1 2 2Z' },
  opinion:     { label: 'Opinión de partido',  color: '#38bdf8', icon: 'M8 14c1.8-3.2 3.3-4.7 5.2-4.7h1.9c1.3 0 2.4 1.1 2.4 2.4v.6h-3.2M8 14l-2.2 3.2M8 14h6.7c1.5 0 2.8 1.2 2.8 2.8V18H9.6M12.5 9.4V6.8c0-1.3 1-2.3 2.3-2.3h.8v4.8' },
  sufrimiento: { label: 'Sufrimiento',         color: '#8b5cf6', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM12 7.1l3.1 2.25-1.2 3.65h-3.8L8.9 9.35 12 7.1Z' },
  humor:       { label: 'Humor',               color: '#f59e0b', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z' },
  festejo:     { label: 'Festejo/Fiesta',      color: '#22c55e', icon: 'M6 20l3-10 5 5-8 5ZM14 4l.4 2.4L17 7l-2.6.6L14 10l-.4-2.4L11 7l2.6-.6L14 4Z' },
  canticos:    { label: 'Cánticos',            color: '#4f46e5', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9.2h.1M16 9.2h.1M7.8 13.2h8.4c-.8 2-2.2 3-4.2 3s-3.4-1-4.2-3Z' },
  bronca:      { label: 'Bronca/Desilusión',   color: '#991b1b', icon: 'M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18ZM8 9l2 1M16 9l-2 1' },
  declaracion: { label: 'Declaración',         color: '#fbbf24', icon: 'M5 6h14v9H9l-4 4V6ZM8 9h8M8 12h6' },
}
const perfilArengaTypes = new Proxy({ ...PERFIL_ARENGA_TYPES_BASE }, { get: (t, k) => t[k] || t.vamos })

export const renderPerfil = async (codigo) => {
  // Fix DT-1: validar grupo antes de cualquier lectura. Si no existe,
  // redirigir a landing en lugar de tirar TypeError.
  const db = getNormalizedDB()
  if (!db.grupos[codigo]) {
    window.location.hash = '/'
    return
  }

  const root = document.getElementById('app-root')
  if (!root) return
  const { grupo, jugador } = profileData(codigo)
  const apodo    = escapePM(jugador.apodo || jugador.nombre || 'Jugador Mundial')
  const nombre   = escapePM(jugador.nombre || '')
  const initials = escapePM((jugador.apodo || jugador.nombre || 'JM').substring(0, 2).toUpperCase())

  // perf-perfil (30-may): skeleton ANTES del fetch (la pantalla anterior ya no
  // queda congelada) + UNA sola llamada al RPC perfil_completo en vez de los ~7
  // round-trips en serie. apodo/nombre/initials van reales en el skeleton
  // (vienen de profileData = localStorage, sin red).
  const playerId = jugador?.player_id || jugador?.id || null
  const currentUser = getCurrentUser()
  root.innerHTML = buildPerfilSkeleton({
    codigo, apodo, nombre, initials,
    activeGroupBar: activeGroupBarPM(codigo),
    bottomNav: bottomNavPM(codigo),
  })

  // RPC perfil_completo: alcance NACIONAL (todos los player_id del user, dedup
  // por user, igual que ranking_nacional). banner rituales = SUM(puntos_total)
  // guardado (idéntico al ranking nacional); las 3 tarjetas (cards) suman EXACTO
  // al banner (la penalización sale del guardado, no se recuenta). Si no hay
  // user logueado o el RPC falla, cae a un perfil vacío coherente.
  // DT-H3-resto: la carga puede lanzar (timeout/error) → "Reintentar" inline.
  // data=null (RPC OK sin datos) sigue cayendo al perfil vacío coherente.
  let perfilData = null
  if (currentUser?.user_id) {
    try {
      perfilData = await getPerfilCompleto(currentUser.user_id, playerId)
    } catch (err) {
      console.error('[perfil] carga falló:', err?.message || err)
      root.innerHTML = retryCardHTML('No se pudo cargar tu perfil') + bottomNavPM(codigo)
      wireRetry(root, () => renderPerfil(codigo))
      return
    }
  }

  const puntos = perfilData?.puntos || { prode: 0, rituales: 0, total: 0 }
  // historyCardsPM espera { tipo: {creadas, reaccionesRecibidas, puntos,
  // [incumplidas]} }. Fusionamos resumen (counts) + cards (puntos atados al
  // guardado, que suman exacto al banner).
  const _res = perfilData?.resumen || {}
  const _cards = perfilData?.cards || {}
  const resumenRit = {
    arenga:  { ...(_res.arenga  || { creadas: 0, reaccionesRecibidas: 0 }), puntos: _cards.arenga  || 0 },
    cabala:  { ...(_res.cabala  || { creadas: 0, reaccionesRecibidas: 0 }), puntos: _cards.cabala  || 0 },
    promesa: { ...(_res.promesa || { creadas: 0, reaccionesRecibidas: 0, incumplidas: 0 }), puntos: _cards.promesa || 0 },
  }
  // Arengas guardadas: el RPC ya trae reacciones agregadas + mi_reaccion +
  // mi_al_pasto. Adaptamos al shape que el render/modal espera (escuchar/pego/
  // loco/pasto planos + miReaccion + miAlPasto) — mismo patrón visual de
  // grupo/Tribuna (4 contadores apagados si mine, 3 botones + Al pasto si ajena).
  const arengasGuardadas = (perfilData?.arengasGuardadas || []).map((a) => ({
    ...a,
    escuchar: a.reacciones?.escuchar || 0,
    pego: a.reacciones?.pego || 0,
    loco: a.reacciones?.loco || 0,
    pasto: a.reacciones?.al_pasto || 0,
    miReaccion: a.mi_reaccion || null,
    miAlPasto: Boolean(a.mi_al_pasto),
  }))

  root.innerHTML = `
                <div class="perfil-v2">
                    ${activeGroupBarPM(codigo)}
                    <section class="perfil-hero">
                        <div class="perfil-player">
                            <div class="perfil-avatar">${initials}</div>
                            <div style="min-width:0;flex:1;">
                                <h2 class="perfil-name">${apodo}</h2>
                                ${nombre ? `<p class="perfil-real-name">${nombre}</p>` : ''}
                            </div>
                            <button class="perfil-whatsapp-btn is-disabled" type="button" disabled aria-disabled="true" title="No disponible todavía">Whatsapp notificaciones<span class="perfil-whatsapp-soon">Próximamente</span></button>
                        </div>
                    </section>
                    <section class="perfil-points" aria-label="Puntos del jugador">
                        <div class="perfil-point main">
                            <strong>${formatPM(puntos.total)}</strong>
                            <span>Puntaje total</span>
                        </div>
                        <div class="perfil-point">
                            <strong>${formatPM(puntos.prode)}</strong>
                            <span>PTS Prode</span>
                        </div>
                        <div class="perfil-point">
                            <strong>${formatPM(puntos.rituales)}</strong>
                            <span>PTS Rituales</span>
                        </div>
                    </section>
                    <button class="perfil-prode-cta" type="button" data-profile-action="my-prode">
                        <span>
                            <strong>Mi Prode Historial</strong>
                            <span>Mi recorrido en el Mundial</span>
                        </span>
                        <strong>Ver</strong>
                    </button>
                    <button class="tribuna-cta tribuna-cta-compact" type="button" data-profile-action="tribuna-reactions">
                        <strong>Mis reacciones tribuneras</strong>
                        <strong>Ver</strong>
                    </button>
                    <h3 class="perfil-section-title">Mi Historial</h3>
                    <section class="perfil-history">
                        ${historyCardsPM(resumenRit)}
                    </section>
                    <!-- 30-may: "Mis guardados de tribuna" eliminado (redundante con
                         "Mis arengas guardadas" abajo, que cumple la misma función). -->
                    <h3 class="perfil-section-title">Arengas guardadas</h3>
                    <section class="perfil-arengas-guardadas" data-perfil-arengas-guardadas>
                        ${renderArengasGuardadasList(arengasGuardadas)}
                    </section>
                    <button class="btn perfil-logout" type="button" data-profile-action="logout">Cerrar sesi&oacute;n</button>
                </div>
                ${bottomNavPM(codigo)}
            `

  root.querySelector('[data-profile-action="my-prode"]')?.addEventListener('click', () => {
    window.location.hash = `/perfil-prode/${codigo}`
  })
  root.querySelector('[data-profile-action="tribuna-reactions"]')?.addEventListener('click', () => {
    window.location.hash = `/perfil-tribuna/${codigo}`
  })
  // 31-may: logout real de Supabase + redirección a welcome ('/') que main.js
  // pinta vía renderLanding cuando authed=false. Antes solo borraba el flag
  // local y mandaba a /ingreso (no deslogueaba en Supabase).
  root.querySelector('[data-profile-action="logout"]')?.addEventListener('click', async () => {
    localStorage.removeItem(`sesion_${codigo}`)
    await signOut()
    // Full reload (audit 5-jun): hash='/' es navegación SPA y NO recarga → el
    // contexto JS persistía y el cliente Supabase quedaba zombie, colgando el
    // próximo login. '/?_=<ts>' fuerza contexto JS fresco → cliente limpio.
    window.location.href = '/?_=' + Date.now()
  })
  // 30-may: botón "Whatsapp notificaciones" deshabilitado (atributo disabled
  // + clase is-disabled). El handler quedó removido — el alert "Pronto vas
  // a poder activar" lo reemplaza la etiqueta "Próximamente" visible en el
  // botón mismo.

  // Spec arengas v1.2: sección "Mis arengas guardadas" con click → modal de
  // despliegue + botón "Quitar de guardadas". Audio reproduce con blob URL
  // (helper compartido del fix e2c1100, cubre archivos con Duration=0 EBML).
  // Set de player_ids del usuario en TODOS los grupos (para detectar arenga
  // propia en el modal de guardadas). Reusa el batch que ya cargamos
  // implícitamente al pedir reacciones mías, pero como acá no devolvió el
  // set directo, lo deriva del propio creador_id de las arengas guardadas
  // junto con jugadores.user_id. Simplificación: usamos el jugadorActivo del
  // grupo (puede haber falsos negativos para creaciones en OTROS grupos del
  // user — en esos casos cae a la rama "ajena", aceptable hasta cubrir con
  // helper dedicado).
  // Set de player_ids del usuario en TODOS los grupos (para detectar arenga
  // propia en el modal de guardadas). Lo trae el RPC perfil_completo (miPlayerIds),
  // así que ya no hace falta la query directa post-paint que había acá antes.
  const miPlayerIds = new Set(perfilData?.miPlayerIds || [playerId].filter(Boolean))
  wireArengasGuardadasSection(root, arengasGuardadas, codigo, currentUser?.user_id, jugador, miPlayerIds)
}

// renderArengasGuardadasList(arengas) — HTML de la sección.
// Empty state si no hay; grid 2 cols con cards reusables (mismo componente
// que la muralla de Tribuna, mode='grid'). En mobile angosto cae a 1 col.
export const renderArengasGuardadasList = (arengas) => {
  if (!arengas || arengas.length === 0) {
    return `
      <div class="perfil-arengas-empty" style="padding:1rem .85rem;border-radius:14px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.82rem;line-height:1.4;text-align:center;">
        Todavía no guardaste ninguna arenga. Cuando escuches una que te guste, tocá Guardar y aparece acá.
      </div>
    `
  }
  return `
    <div class="perfil-arengas-grid" style="display:grid;grid-template-columns:repeat(2, minmax(0, 1fr));gap:.5rem;">
      ${arengas.map((a) => renderArengaCard({
        arenga: {
          id: a.arenga_id,
          tipo: a.tipo_arenga,
          duracion: `0:${String(a.duracion_segundos || 0).padStart(2, '0')}`,
          grupoOrigen: a.grupo_nombre,
        },
        mode: 'grid',
        className: 'perfil-arenga-guardada-card',
        clickHandler: '',
        // Segunda línea: "De Juan · hace X" (en lugar de "N reac. · hace X").
        secondaryLine: `
          <em style="color:var(--text-muted);font-style:normal;font-size:0.62rem;line-height:1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;min-width:0;">De ${escapePM(a.creador_nombre)}</em>
          <em style="color:var(--text-muted);font-style:normal;font-size:0.62rem;line-height:1;white-space:nowrap;">${formatTimeAgo(a.saved_at)}</em>
        `,
      })).join('')}
    </div>
  `
}

// wireArengasGuardadasSection(root, arengas, codigo, userId, jugador) — bindea
// clicks en cards → modal de despliegue con audio player + botón "Quitar".
// Las cards usan data-id (convención del componente src/ui/arenga-card.js).
const wireArengasGuardadasSection = (root, arengas, codigo, userId, jugador, miPlayerIds) => {
  const cards = root.querySelectorAll('.perfil-arenga-guardada-card')
  cards.forEach((card) => {
    card.addEventListener('click', () => {
      const id = card.dataset.id
      const arenga = arengas.find((a) => a.arenga_id === id)
      if (!arenga) return
      const isOwn = miPlayerIds.has(arenga.creador_id)
      openPerfilArengaModal(arenga, codigo, userId, jugador, isOwn, miPlayerIds)
    })
  })
}

// Reacciones canónicas de arenga (slugs internos consistentes con
// rituales.js / tribuna.js). Iconos del renderPactoIcon — paths idénticos.
const ARENGA_REACTIONS = [
  { id: 'escuchar', label: 'Escuchen esto', icon: 'M6 12h7l5-7v14l-5-7H6ZM4 9v6', color: '#75aadb' },
  { id: 'pego',     label: 'Vamo arriba !', icon: 'M12 3c3 3 4 5.5 2 8 2-.5 3-2 3-4 2 2 3 4.4 3 7a8 8 0 1 1-16 0c0-3 1.4-5.4 4-7-.2 2.4.7 4 2.5 5C9.8 8.5 10.5 5.5 12 3Z', color: '#fb923c' },
  { id: 'loco',     label: 'Jaja !', icon: 'M7 10h10M8 14c2.4 2.2 5.6 2.2 8 0M8.5 8h.1M15.5 8h.1M12 21a9 9 0 1 0 0-18 9 9 0 0 0 0 18Z', color: '#facc15' },
  { id: 'pasto',    label: 'Te fuiste al pasto', icon: 'M5 18h9c2.4 0 4-1.6 4-4v-1h-5l-2-6H8l1.8 6H5v5Z', color: '#c084fc' },
]
const arengaReacTotal = (a, reactId) => reactId === 'pasto' ? (a.pasto || 0) : (a[reactId] || 0)

// buildPerfilArengaModalHTML(arenga, isOwn) — modal de despliegue idéntico
// en estructura a renderArengaDetail de rituales.js: respeta creador
// (4 contadores apagados + cartel + botones gestión) vs no-creador
// (3 botones reactivos + Al pasto + Puntos: +X). "Quitar de guardadas"
// se agrega como acción extra del contexto-perfil (suma, no reemplaza).
export const buildPerfilArengaModalHTML = (arenga, isOwn = false) => {
  const meta = perfilArengaTypes[arenga.tipo_arenga] || perfilArengaTypes.vamos
  const titulo = (arenga.titulo && arenga.titulo.trim().length) ? arenga.titulo : meta.label
  const audioUrl = arenga.audio_url && arenga.audio_url !== 'pendiente' ? arenga.audio_url : ''
  const segundos = arenga.duracion_segundos || 0
  const duracion = `0:${String(segundos).padStart(2, '0')}`
  const totalReac = (arenga.escuchar || 0) + (arenga.pego || 0) + (arenga.loco || 0) + (arenga.pasto || 0)
  const pts = puntosPorRitual({ reaccionesTotal: totalReac })
  const visibilidadActual = arenga.es_publica ? 'publica' : 'grupo'
  return `
    <div class="glass-card" style="width:min(100%,420px);max-height:calc(100vh - 7rem);overflow-y:auto;padding:.78rem;border-color:${meta.color}55;background:radial-gradient(circle at 18% 0%, ${meta.color}24, transparent 34%), rgba(12,18,30,.96);border-radius:14px;">
      <div style="display:flex;justify-content:space-between;align-items:flex-start;gap:.65rem;margin-bottom:.58rem;">
        <div style="display:flex;gap:.58rem;align-items:center;min-width:0;">
          <span style="width:46px;height:46px;border-radius:14px;display:inline-flex;align-items:center;justify-content:center;background:linear-gradient(135deg,${meta.color} 0%,rgba(255,255,255,.14) 100%);border:1px solid rgba(255,255,255,.18);">${renderPactoIcon(meta.icon, '#ffffff', 24)}</span>
          <div style="min-width:0;">
            <span style="display:block;color:${meta.color};font-weight:900;font-size:.66rem;text-transform:uppercase;">${meta.label}</span>
            <h3 style="margin:.12rem 0 0;color:var(--white);font-family:var(--font-heading);font-size:1rem;line-height:1.02;overflow-wrap:anywhere;">${escapePM(titulo)}</h3>
          </div>
        </div>
        <button class="btn btn-secondary" id="perfilArengaModalClose" style="width:auto;min-height:30px;margin:0;padding:.28rem .52rem;border-radius:999px;">X</button>
      </div>

      <div style="padding:.58rem;border-radius:11px;background:rgba(255,255,255,.052);border:1px solid rgba(255,255,255,.09);">
        ${audioUrl ? `<audio id="perfilArengaAudio" preload="metadata" src="${audioUrl}" style="display:none;"></audio>` : ''}
        <div style="display:flex;align-items:center;gap:.6rem;">
          <button id="perfilArengaPlayBtn" class="btn btn-primary" ${audioUrl ? '' : 'disabled'} style="width:42px;height:42px;min-height:42px;border-radius:50%;margin:0;padding:0;display:flex;align-items:center;justify-content:center;font-size:.76rem;background:linear-gradient(135deg,${meta.color} 0%,var(--celeste) 100%);border:none;${audioUrl ? '' : 'opacity:.45;'}">Play</button>
          <div style="flex:1;min-width:0;">
            <div style="height:6px;border-radius:999px;overflow:hidden;background:rgba(255,255,255,.12);">
              <div id="perfilArengaProgress" style="width:0%;height:100%;background:linear-gradient(90deg,${meta.color},var(--celeste));transition:width .25s linear;"></div>
            </div>
            <div style="display:flex;justify-content:space-between;color:var(--text-muted);font-size:.64rem;margin-top:.32rem;">
              <span>0:00</span><span>${duracion}</span>
            </div>
          </div>
        </div>
        ${audioUrl ? '' : '<span style="display:block;color:var(--text-muted);font-size:.66rem;margin-top:.34rem;">Audio aún no subido.</span>'}
      </div>

      <div style="display:flex;flex-wrap:wrap;gap:.34rem;margin-top:.5rem;align-items:center;">
        <span style="font-size:.7rem;color:var(--text-muted);">De ${escapePM(arenga.creador_nombre)}</span>
        <span style="font-size:.7rem;color:var(--text-muted);">${escapePM(arenga.grupo_nombre)}</span>
      </div>
      <div style="margin-top:.32rem;display:flex;justify-content:space-between;gap:.5rem;font-size:.7rem;">
        <span style="color:var(--text-muted);">${formatTimeAgo(arenga.created_at)}</span>
        <span data-rit-pts="arenga:${arenga.arenga_id}" data-rit-reac="${totalReac}" style="color:#7ab488;">Puntos: ${pts > 0 ? '+' : ''}${pts}</span>
      </div>

      ${isOwn ? `
        <div style="margin-top:.58rem;padding:.36rem .5rem;border-radius:11px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);color:var(--text-muted);font-size:.66rem;text-align:center;">Es tu arenga — no podés reaccionar a vos mismo.</div>
        <div style="display:grid;grid-template-columns:repeat(4, minmax(0, 1fr));gap:0.3rem;margin-top:0.4rem;">
          ${ARENGA_REACTIONS.map((r) => `
            <div aria-disabled="true" style="margin:0;min-height:36px;padding:.26rem .2rem;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.08);border-radius:10px;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.1rem;opacity:.55;cursor:default;user-select:none;">
              <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(r.icon, r.color, 11)} ${r.label}</span>
              <span style="color:${r.color};font-size:.64rem;font-weight:900;">${arengaReacTotal(arenga, r.id)}</span>
            </div>
          `).join('')}
        </div>
      ` : `
        <div style="display:grid;grid-template-columns:repeat(3, minmax(0, 1fr));gap:.3rem;margin-top:.58rem;">
          ${ARENGA_REACTIONS.filter((r) => r.id !== 'pasto').map((r) => {
            const active = arenga.miReaccion === r.id
            const bg = active ? `${r.color}29` : (r.id === 'escuchar' ? 'rgba(117,170,219,0.12)' : 'rgba(255,255,255,0.055)')
            const border = active ? `${r.color}cc` : (r.id === 'escuchar' ? 'rgba(117,170,219,0.3)' : 'rgba(255,255,255,0.1)')
            return `<button class="btn btn-secondary perfil-arenga-react${active ? ' perfil-arenga-react-active' : ''}" data-id="${arenga.arenga_id}" data-reaction="${r.id}" data-active="${active ? '1' : '0'}" style="margin:0;min-height:36px;padding:.26rem .2rem;background:${bg};border-color:${border};${active ? 'box-shadow:0 0 0 1px ' + r.color + '55 inset;' : ''}display:flex;flex-direction:column;align-items:center;justify-content:center;gap:.1rem;">
              <span style="display:flex;align-items:center;gap:.12rem;color:var(--white);font-size:.58rem;line-height:1;white-space:nowrap;">${renderPactoIcon(r.icon, r.color, 11)} ${r.label}</span>
              <span style="color:${r.color};font-size:.64rem;font-weight:900;">${arengaReacTotal(arenga, r.id)}</span>
            </button>`
          }).join('')}
        </div>
        <div style="display:grid;grid-template-columns:3fr 1fr;gap:.38rem;margin-top:.42rem;">
          ${(arenga.es_publica && arenga.audio_id) ? `<button id="perfilArengaShareBtn" type="button" class="btn btn-secondary" style="margin:0;min-height:34px;padding:.4rem .55rem;background:rgba(34,197,94,.08);border:1px solid rgba(34,197,94,.18);color:#bbf7d0;font-size:.7rem;">🔗 Compartir</button>` : '<span></span>'}
          <button class="btn btn-secondary perfil-arenga-react${arenga.miAlPasto ? ' perfil-arenga-react-active' : ''}" data-id="${arenga.arenga_id}" data-reaction="pasto" data-active="${arenga.miAlPasto ? '1' : '0'}" style="margin:0;min-height:34px;padding:.38rem .22rem;background:rgba(192,132,252,${arenga.miAlPasto ? '0.22' : '0.08'});border-color:rgba(192,132,252,${arenga.miAlPasto ? '0.6' : '0.18'});color:#ddd6fe;font-size:.62rem;display:flex;align-items:center;justify-content:center;gap:.28rem;${arenga.miAlPasto ? 'box-shadow:0 0 0 1px rgba(192,132,252,.35) inset;' : ''}"><span>${arenga.miAlPasto ? '✓ Reportado' : '🥾 Al pasto'}</span><span style="color:#c084fc;font-weight:900;">${arenga.pasto || 0}</span></button>
        </div>
      `}

      ${isOwn ? `
        <div style="margin-top:.42rem;">
          <button class="btn btn-secondary perfil-arenga-eliminar" data-id="${arenga.arenga_id}" style="margin:0;width:100%;min-height:34px;padding:.4rem .42rem;border-radius:999px;background:rgba(248,113,113,.08);border:1px solid rgba(248,113,113,.22);color:#fecaca;font-size:.66rem;font-weight:900;">🗑️ Eliminar</button>
        </div>
      ` : ''}

      <button id="perfilArengaQuitarBtn" type="button" class="btn btn-secondary" style="width:100%;margin:.42rem 0 0;min-height:36px;padding:.42rem .58rem;border-radius:999px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.12);color:var(--text-muted);font-size:.68rem;">🗑️ Quitar de guardadas</button>
    </div>
  `
}

// openPerfilArengaModal(arenga, codigo, userId, jugador) — monta el modal
// en el DOM y bindea handlers. HTML generado por buildPerfilArengaModalHTML
// (pura, testeable).
export const openPerfilArengaModal = (arenga, codigo, userId, jugador, isOwn = false, miPlayerIds = new Set()) => {
  closePerfilArengaModal() // idempotente: cierra cualquier modal previo
  const overlay = document.createElement('div')
  overlay.id = 'perfilArengaModal'
  overlay.dataset.arengaDetailOpen = '1' // marker para refrescarContadoresRitual (mosaico Tribuna no aplica acá pero queda coherente)
  overlay.style.cssText = 'position:fixed;inset:0;z-index:90;background:rgba(0,0,0,.55);display:flex;align-items:flex-start;justify-content:center;padding:6rem .72rem .72rem;overflow-y:auto;'
  overlay.innerHTML = buildPerfilArengaModalHTML(arenga, isOwn)
  overlay.addEventListener('click', (event) => {
    if (event.target === overlay) closePerfilArengaModal()
  })
  document.body.appendChild(overlay)
  wirePerfilArengaModal(arenga, codigo, userId, isOwn, miPlayerIds)
}

const closePerfilArengaModal = () => {
  const existing = document.getElementById('perfilArengaModal')
  if (existing) {
    const a = existing.querySelector('#perfilArengaAudio')
    if (a) {
      try { a.pause() } catch (_) {}
      revokeBlobUrl(a)
    }
    existing.remove()
  }
}

const wirePerfilArengaModal = (arenga, codigo, userId, isOwn = false, miPlayerIds = new Set()) => {
  const overlay = document.getElementById('perfilArengaModal')
  if (!overlay) return
  overlay.querySelector('#perfilArengaModalClose')?.addEventListener('click', () => closePerfilArengaModal())

  const audioEl = overlay.querySelector('#perfilArengaAudio')
  const playBtn = overlay.querySelector('#perfilArengaPlayBtn')
  const progressEl = overlay.querySelector('#perfilArengaProgress')
  if (audioEl && playBtn && progressEl) {
    let rafId = null
    let startMs = 0
    let accumMs = 0
    const stop = () => { if (rafId !== null) { cancelAnimationFrame(rafId); rafId = null } }
    const finalize = () => {
      try { audioEl.pause() } catch (_) {}
      stop(); startMs = 0; accumMs = 0
      playBtn.textContent = 'Play'
      progressEl.style.width = '0%'
    }
    const segundos = arenga.duracion_segundos || 0
    const update = () => {
      const dur = audioEl.duration
      const ct = audioEl.currentTime
      let elapsed, total
      if (Number.isFinite(dur) && dur > 0 && ct > 0) { elapsed = ct; total = dur }
      else {
        const live = startMs > 0 ? (Date.now() - startMs) : 0
        elapsed = (accumMs + live) / 1000
        total = segundos || 1
      }
      const pct = Math.min(100, (elapsed / total) * 100)
      progressEl.style.width = `${pct}%`
      if (pct >= 100 && (!Number.isFinite(dur) || dur <= 0)) finalize()
    }
    const tick = () => {
      update()
      if (rafId !== null && !audioEl.paused && !audioEl.ended) rafId = requestAnimationFrame(tick)
    }
    audioEl.addEventListener('pause', () => {
      stop()
      if (startMs > 0) { accumMs += Date.now() - startMs; startMs = 0 }
    })
    audioEl.addEventListener('ended', finalize)
    playBtn.addEventListener('click', async () => {
      if (audioEl.paused) {
        playBtn.textContent = 'Pausa'
        const blob = await ensureBlobUrl(audioEl)
        if (!blob.ok && !blob.cached) console.warn('[perfil] ensureBlobUrl:', blob.reason)
        if (audioEl.readyState === 0) audioEl.load()
        const p = audioEl.play()
        if (p && typeof p.catch === 'function') p.catch((err) => {
          console.error('[perfil] audio.play():', err)
          playBtn.textContent = 'Play'
          stop(); startMs = 0
        })
        startMs = Date.now()
        if (rafId === null) rafId = requestAnimationFrame(tick)
      } else {
        audioEl.pause()
        playBtn.textContent = 'Play'
      }
    })
  }

  // Spec arengas v1.2 §10 regla 15: share unificado vía modal con WhatsApp /
  // Telegram / Copiar / Cancelar. Solo arengas públicas; las privadas no
  // muestran botón (el HTML del modal usa el condicional es_publica && audio_id).
  overlay.querySelector('#perfilArengaShareBtn')?.addEventListener('click', () => {
    const titulo = (arenga.titulo && arenga.titulo.trim().length) ? arenga.titulo : 'arenga mundialista'
    const creador = arenga.creador_nombre || 'la tribuna'
    const url = buildShareUrl(arenga.audio_id)
    openShareModal({
      title: `Compartir arenga · ${titulo}`,
      subtitle: `De ${creador}`,
      shareUrl: url,
      shareText: `Escuchá la arenga de ${creador}: "${titulo}"`,
    })
  })

  // Handler reacciones (no-creador): mismo patrón que rituales.js / tribuna.js.
  // Optimistic + refrescarContadoresRitual (actualiza "Puntos: +X" in-place)
  // + bloqueo Al pasto repetido. Necesita un player_id válido del user; uso
  // el primero de miPlayerIds (el RPC reaccionar valida auth.uid()=user_id).
  const playerIdParaReaccionar = !isOwn && miPlayerIds.size ? miPlayerIds.values().next().value : null
  overlay.querySelectorAll('.perfil-arenga-react').forEach((button) => {
    button.addEventListener('click', async () => {
      if (!playerIdParaReaccionar || button.disabled) return
      const reaction = button.dataset.reaction
      const isActive = button.dataset.active === '1'
      const arengaId = button.dataset.id
      if (reaction === 'pasto' && isActive) return
      const prevActive = overlay.querySelector(`.perfil-arenga-react[data-id="${arengaId}"][data-active="1"]`)
      const prevActiveReaction = prevActive ? prevActive.dataset.reaction : null
      const deltas = computeReactionDelta({ clickedReaction: reaction, isActiveOnClicked: isActive, prevActiveReaction })
      applyReactionDelta({ scope: document, selector: '.perfil-arenga-react', arengaId, deltas })
      const sumaDelta = deltas.reduce((s, d) => s + d.delta, 0)
      refrescarContadoresRitual('arenga', arengaId, sumaDelta)
      button.disabled = true
      button.style.opacity = '.5'
      let r
      if (reaction === 'pasto') r = await reaccionar(playerIdParaReaccionar, 'arenga', arengaId, 'al_pasto')
      else if (isActive) r = await quitarReaccion(playerIdParaReaccionar, 'arenga', arengaId)
      else r = await reaccionar(playerIdParaReaccionar, 'arenga', arengaId, reaction)
      if (r.error) {
        applyReactionDelta({ scope: document, selector: '.perfil-arenga-react', arengaId, deltas: invertDeltas(deltas) })
        refrescarContadoresRitual('arenga', arengaId, -sumaDelta)
        button.disabled = false
        button.style.opacity = '1'
        alert('⚠️ No pudimos guardar tu reacción. Probá de nuevo.')
        return
      }
      button.disabled = false
      button.style.opacity = '1'
      if (reaction === 'pasto') {
        button.dataset.active = '1'
        button.style.background = 'rgba(192,132,252,0.22)'
        button.style.borderColor = 'rgba(192,132,252,0.6)'
        button.style.boxShadow = '0 0 0 1px rgba(192,132,252,.35) inset'
        const labelSpan = button.querySelector('span')
        if (labelSpan) labelSpan.textContent = '✓ Reportado'
      } else {
        overlay.querySelectorAll(`.perfil-arenga-react[data-id="${arengaId}"]`).forEach((b) => {
          if (b.dataset.reaction === 'pasto') return
          b.dataset.active = (b === button && !isActive) ? '1' : '0'
        })
      }
    })
  })

  // Cambiar visibilidad después de crear quedó deshabilitado (decisión 9-jun):
  // se decide SOLO al crear. Botón "Hacer pública/privada" y handler removidos.
  // Eliminar arenga propia.
  overlay.querySelector('.perfil-arenga-eliminar')?.addEventListener('click', async (e) => {
    const btn = e.currentTarget
    if (btn.disabled) return
    if (!confirm('¿Eliminar la arenga? Esta acción no se puede deshacer.')) return
    btn.disabled = true
    btn.style.opacity = '.5'
    const r = await eliminarArengaCompleta(arenga.arenga_id)
    if (r.error) {
      btn.disabled = false
      btn.style.opacity = '1'
      alert('⚠️ No pudimos eliminarla. Probá de nuevo.')
      return
    }
    closePerfilArengaModal()
    renderPerfil(codigo)
  })

  overlay.querySelector('#perfilArengaQuitarBtn')?.addEventListener('click', async () => {
    const btn = overlay.querySelector('#perfilArengaQuitarBtn')
    if (btn.disabled) return
    btn.disabled = true
    btn.style.opacity = '.5'
    const r = await quitarArengaGuardada(userId, arenga.arenga_id)
    if (r.error) {
      btn.disabled = false
      btn.style.opacity = '1'
      alert('⚠️ No pudimos quitarla. Probá de nuevo.')
      return
    }
    closePerfilArengaModal()
    // Re-render del perfil entero para reflejar el cambio sin estado stale.
    renderPerfil(codigo)
  })
}

// ─── Bridge transitorio a window.* (sub-fase 3.7 → FASE 4) ─────────────────
// Patrón C1 (REFACTOR_AUDIT.md §14.1): el router central despacha
// window.renderPerfil para hash '/perfil/<codigo>'. En 3.15 (main.js)
// el router pasará a importar directamente y este bridge desaparece.

if (typeof window !== 'undefined') {
  window.renderPerfil = renderPerfil
}
